import React, { useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import useFetchById from 'hooks/useFetchById';
import useUpdate from 'hooks/useUpdate';

import ShelfViewMode from 'components/admin/shelf/ShelfDetail/ShelfViewMode';
import ShelfEditForm from 'components/admin/shelf/ShelfDetail/ShelfEditForm';
import Table from 'components/common/Table';

import { adminShelfApi, inventoryApi } from 'api';
import {
  INVENTORY_LIST_COLUMNS,
  INVENTORY_FILTER_OPTIONS,
} from 'constants/inventory';
import { toastApiError } from 'utils/apiErrors';
import { useUserContext } from 'providers/UserProvider';
import paths from 'routes/paths';

import { isValidValue, parseFormValue } from 'utils/valueFormatters';

const ShelfDetail = () => {
  const { shelfId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const { selectedTenant, selectedWarehouse } = useUserContext();

  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const {
    data: shelf,
    loading: fetchLoading,
    error: fetchError,
    refetch,
  } = useFetchById(adminShelfApi, shelfId);

  const {
    update,
    loading: updateLoading,
    error: updateError,
  } = useUpdate(adminShelfApi, 'patchForm');

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleDeactivate = async () => {
    try {
      await adminShelfApi.delete(shelfId);
      await refetch();
      toast.success('Shelf deactivated successfully');
    } catch (err) {
      toastApiError(err, 'Failed to deactivate shelf');
    }
  };

  const handleSubmit = async (e, imageState) => {
    e.preventDefault();

    try {
      const formData = new FormData(e.target);

      const fieldTypes = {
        name: 'string',
        description: 'string',
        is_active: 'boolean',
      };

      Object.entries(fieldTypes).forEach(([field, type]) => {
        const value = formData.get(field);
        if (isValidValue(value)) {
          formData.set(field, parseFormValue(value, type));
        }
      });

      if (imageState.hasChanged) {
        if (imageState.isRemoved) {
          formData.set('location_image', '');
        } else if (imageState.file) {
          formData.set('location_image', imageState.file);
        }
      }

      await update(shelfId, formData);
      await refetch();

      setIsEditing(false);
      toast.success('Shelf updated successfully');
    } catch (err) {
      toastApiError(err, 'Failed to update shelf');
    }
  };

  const closeModal = useCallback(() => {
    setIsEditing(false);
  }, []);

  const inventoryFilter = useMemo(
    () => (shelf ? { shelf_code__icontains: shelf.code } : {}),
    [shelf],
  );

  if (fetchLoading) {
    return (
      <Card className="h-100">
        <Card.Body>
          <div className="text-center">Loading...</div>
        </Card.Body>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className="h-100">
        <Card.Body>
          <Alert variant="danger">{fetchError}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Header className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Shelf Detail</h5>

            {!isEditing && (
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  as={Link}
                  to={`${paths.inventory.list(
                    tenant,
                    warehouse,
                  )}?shelf_code__icontains=${encodeURIComponent(
                    shelf?.code ?? '',
                  )}`}
                >
                  View Inventory
                </Button>
                <Button variant="primary" size="sm" onClick={handleEditClick}>
                  <FontAwesomeIcon icon="edit" className="me-2" />
                  Edit
                </Button>

                {shelf?.is_active && (
                  <Button variant="danger" size="sm" onClick={handleDeactivate}>
                    <FontAwesomeIcon icon="ban" className="me-2" />
                    Deactivate
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card.Header>

        <Card.Body>
          {updateError && <Alert variant="danger">{updateError}</Alert>}

          {isEditing ? (
            <ShelfEditForm
              shelf={shelf}
              onSubmit={handleSubmit}
              onCancel={closeModal}
              isLoading={updateLoading}
            />
          ) : (
            <ShelfViewMode shelf={shelf} />
          )}
        </Card.Body>
      </Card>

      {shelf && (
        <Table
          title="Inventory"
          apiService={inventoryApi}
          columns={INVENTORY_LIST_COLUMNS}
          filterOptions={INVENTORY_FILTER_OPTIONS}
          initialFilter={inventoryFilter}
          sortable={true}
          initialPageSize={25}
        />
      )}
    </>
  );
};

export default ShelfDetail;
